import { fieldMap } from "./fieldMap";
import { getDispatchKeys, parseTimeHHMM, resolveUnitField } from "./utils";
import { BODY_INJURY_MARKS_KEY, BURN_ZONES_KEY } from "./bodyInjuries";
import type { PdfFormDefinition, PdfImportIds, DispatchFn, RpcFn } from "./types";

export async function importForm001Fields(
  form: PdfFormDefinition,
  fields: Record<string, string | boolean>,
  ids: PdfImportIds,
  dispatch: DispatchFn,
  rpc: RpcFn,
  createUuid: () => string,
): Promise<void> {
  // Unit lookup/create must run first — it uses rpc (needs a server response)
  // before we can determine the unitUuid to dispatch patient.setUnit
  const unitNameValue = fields["unitName"];
  if (unitNameValue && typeof unitNameValue === "string") {
    await resolveUnitField(unitNameValue, ids.patientUuid, dispatch, rpc, createUuid);
  }

  // Collect all remaining dispatches synchronously — no intermediate awaits.
  // Because dispatch() queues via queueMicrotask, all calls issued in the same
  // JS turn are batched into a single WebSocket message by the sync engine.
  // The server processes commands in array order, so create-before-set ordering
  // is preserved as long as we push in the right order.
  const pending: Promise<unknown>[] = [];
  const d = (p: Promise<unknown>) => { pending.push(p); };

  // Section 16 — R2 exam entity (create before generic dispatch so examUuid is available)
  const examKeys = getDispatchKeys(form).filter((k) => fieldMap[k]?.command.startsWith("exam."));
  const hasExamData = examKeys.some((k) => {
    const v = fields[k];
    return v !== undefined && v !== "" && v !== false;
  });
  const examUuid = hasExamData ? createUuid() : undefined;
  if (hasExamData && examUuid) {
    d(dispatch("exam.create", { uuid: examUuid, caseUuid: ids.caseUuid, role: 2 }));
  }

  // Section 18 — R2 surgery entity (create before generic dispatch so surgeryUuid is available)
  const surgeryKeys = getDispatchKeys(form).filter((k) => fieldMap[k]?.command.startsWith("surgery."));
  const hasSurgeryData = surgeryKeys.some((k) => {
    const v = fields[k];
    return v !== undefined && v !== "" && v !== false;
  });
  const surgeryUuid = hasSurgeryData ? createUuid() : undefined;
  if (hasSurgeryData && surgeryUuid) {
    d(dispatch("surgery.create", { uuid: surgeryUuid, caseUuid: ids.caseUuid, role: 2 }));
  }

  const idsResolved: PdfImportIds = { ...ids, examUuid, surgeryUuid };

  // Generic fieldMap dispatch
  for (const key of getDispatchKeys(form)) {
    const value = fields[key];
    if (value === undefined || value === "" || value === false) continue;
    const mapping = fieldMap[key];
    if (!mapping) continue;
    const payload = mapping.getPayload(value, idsResolved);
    if (payload) d(dispatch(mapping.command, payload));
  }

  // Section 18.1 — R2 tourniquet application times (per-limb entity creation)
  for (const limb of ["rue", "lue", "rle", "lle"] as const) {
    const appText = fields[`p2-tq-${limb}-app`] as string | undefined;
    const appTime = fields[`p2-tq-${limb}-app-time`] as string | undefined;
    if (!appText && !appTime) continue;
    const tqUuid = createUuid();
    d(dispatch("tourniquet.create", { uuid: tqUuid, caseUuid: ids.caseUuid, role: 2, limb }));
    const ts = parseTimeHHMM(appTime ?? "");
    if (ts !== null) d(dispatch("tourniquet.setApplicationTime", { uuid: tqUuid, timestamp: ts }));
  }

  // Section 4.1 — R1 tourniquet times (per-limb entity creation)
  for (const limb of ["rue", "lue", "rle", "lle"] as const) {
    const cols = ["app", "rem", "conv", "repo"] as const;
    const commands = {
      app: "tourniquet.setApplicationTime",
      rem: "tourniquet.setRemovalTime",
      conv: "tourniquet.setConversionTime",
      repo: "tourniquet.setRepositioningTime",
    } as const;
    const vals = cols.map((c) => fields[`p1-tq-${limb}-${c}`] as string | undefined);
    if (!vals.some((v) => v)) continue;
    const tqUuid = createUuid();
    d(dispatch("tourniquet.create", { uuid: tqUuid, caseUuid: ids.caseUuid, role: 1, limb }));
    for (let i = 0; i < cols.length; i++) {
      const ts = parseTimeHHMM(vals[i] ?? "");
      if (ts !== null) d(dispatch(commands[cols[i]], { uuid: tqUuid, timestamp: ts }));
    }
  }

  // Section 5 — R1 vital signs (3 rows)
  const vsNumCols: Array<[string, string]> = [
    ["rr",       "vitals.setRespiratoryRate"],
    ["spo2",     "vitals.setSpO2"],
    ["pulse",    "vitals.setPulse"],
    ["sys",      "vitals.setSystolic"],
    ["dia",      "vitals.setDiastolic"],
    ["gcs",      "vitals.setGcsEye"],
    ["pain",     "vitals.setPainScale"],
    ["temp",     "vitals.setTemperature"],
    ["diuresis", "vitals.setDiuresis"],
  ];
  for (let i = 0; i < 3; i++) {
    const timeVal = fields[`p1-vs-${i}-time`] as string | undefined;
    const hasAny = (timeVal && parseTimeHHMM(timeVal) !== null) ||
      vsNumCols.some(([k]) => { const v = fields[`p1-vs-${i}-${k}`] as string | undefined; return !!v && !isNaN(parseFloat(v)); });
    if (!hasAny) continue;
    const vsUuid = createUuid();
    d(dispatch("vitals.create", { uuid: vsUuid, caseUuid: ids.caseUuid, role: 1 }));
    const ts = parseTimeHHMM(timeVal ?? "");
    if (ts !== null) d(dispatch("vitals.setTime", { uuid: vsUuid, timestamp: ts }));
    for (const [k, cmd] of vsNumCols) {
      const n = Number(fields[`p1-vs-${i}-${k}`]);
      if (!isNaN(n) && fields[`p1-vs-${i}-${k}`]) d(dispatch(cmd, { uuid: vsUuid, value: n }));
    }
  }

  // Section 6 — R1 ventilation parameters (3 rows)
  const ventNumCols: Array<[string, string]> = [
    ["rate",  "ventilation.setRate"],
    ["tv",    "ventilation.setTidalVolume"],
    ["fio2",  "ventilation.setFiO2"],
    ["peep",  "ventilation.setPeep"],
    ["pip",   "ventilation.setPip"],
    ["etco2", "ventilation.setEtCO2"],
  ];
  for (let i = 0; i < 3; i++) {
    const timeVal = fields[`p1-vent-${i}-time`] as string | undefined;
    const modeVal = fields[`p1-vent-${i}-mode`] as string | undefined;
    const hasAny = (timeVal && parseTimeHHMM(timeVal) !== null) ||
      (modeVal && !/^[.:\/]$/.test(modeVal.trim())) ||
      ventNumCols.some(([k]) => { const v = fields[`p1-vent-${i}-${k}`] as string | undefined; return !!v && !isNaN(parseFloat(v)); });
    if (!hasAny) continue;
    const ventUuid = createUuid();
    d(dispatch("ventilation.create", { uuid: ventUuid, caseUuid: ids.caseUuid, role: 1 }));
    const ts = parseTimeHHMM(timeVal ?? "");
    if (ts !== null) d(dispatch("ventilation.setTime", { uuid: ventUuid, timestamp: ts }));
    if (modeVal) d(dispatch("ventilation.setMode", { uuid: ventUuid, mode: modeVal }));
    for (const [k, cmd] of ventNumCols) {
      const n = Number(fields[`p1-vent-${i}-${k}`]);
      if (!isNaN(n) && fields[`p1-vent-${i}-${k}`]) d(dispatch(cmd, { uuid: ventUuid, value: n }));
    }
  }

  // Section 7 — R1 CPR
  const cprStart = fields["p1-cpr-start"] as string | undefined;
  const cprEnd   = fields["p1-cpr-end"]   as string | undefined;
  if (cprStart || cprEnd) {
    const cprUuid = createUuid();
    d(dispatch("cpr.create", { uuid: cprUuid, caseUuid: ids.caseUuid, role: 1 }));
    const tsStart = parseTimeHHMM(cprStart ?? "");
    const tsEnd   = parseTimeHHMM(cprEnd   ?? "");
    if (tsStart !== null) d(dispatch("cpr.setStartTime", { uuid: cprUuid, timestamp: tsStart }));
    if (tsEnd   !== null) d(dispatch("cpr.setEndTime",   { uuid: cprUuid, timestamp: tsEnd }));
  }

  // Section 9 — Diagnostics
  const diagTextCols: [string, string][] = [
    ["p1-diag-xray",       "diagnostics.setXray"],
    ["p1-diag-ecg",        "diagnostics.setEcg"],
    ["p1-diag-otoscopy-r", "diagnostics.setOtoscopyRight"],
    ["p1-diag-otoscopy-l", "diagnostics.setOtoscopyLeft"],
    ["p1-diag-notes",      "diagnostics.setNotes"],
    ["p1-diag-other",      "diagnostics.setOther"],
  ];
  const diagCbCols: [string, string, string][] = [
    ["p1-diag-xray-cb",       "diagnostics.setXray",         "+"],
    ["p1-diag-otoscopy-r-cb", "diagnostics.setOtoscopyRight", "+"],
    ["p1-diag-otoscopy-l-cb", "diagnostics.setOtoscopyLeft",  "+"],
    ["p1-diag-ecg-cb",        "diagnostics.setEcg",          "+"],
    ["p1-diag-pupils-cb",     "diagnostics.setPupils",       "+"],
  ];
  const eFastVal = fields["p1-diag-efast-pos"] ? "+" : fields["p1-diag-efast-neg"] ? "-" : fields["p1-diag-efast-unk"] ? "+/-" : null;
  const hasAnyDiag = eFastVal || diagTextCols.some(([k]) => fields[k]) || diagCbCols.some(([k]) => fields[k]);
  if (hasAnyDiag) {
    const diagUuid = createUuid();
    d(dispatch("diagnostics.create", { uuid: diagUuid, caseUuid: ids.caseUuid, role: 1 }));
    if (eFastVal) d(dispatch("diagnostics.setEFast", { uuid: diagUuid, value: eFastVal }));
    for (const [k, cmd] of diagTextCols) {
      const val = fields[k] as string | undefined;
      if (val) d(dispatch(cmd, { uuid: diagUuid, value: val }));
    }
    for (const [k, cmd, val] of diagCbCols) {
      // Only use checkbox if the corresponding text field is empty
      if (fields[k] && !fields[k.replace("-cb", "")]) d(dispatch(cmd, { uuid: diagUuid, value: val }));
    }
  }

  // Section 10 — R1 medications (left role=1, right role=11; 5 rows each)
  for (const [side, role] of [["left", 1], ["right", 11]] as [string, number][]) {
    for (let i = 0; i < 5; i++) {
      const prefix = `p1-med-${side}-${i}`;
      const nameVal  = fields[`${prefix}-name`]  as string | undefined;
      const doseVal  = fields[`${prefix}-dose`]  as string | undefined;
      const routeVal = fields[`${prefix}-route`] as string | undefined;
      const timeVal  = fields[`${prefix}-time`]  as string | undefined;
      if (!nameVal && !doseVal && !routeVal && !timeVal) continue;
      const medUuid = createUuid();
      d(dispatch("medication.create", { uuid: medUuid, caseUuid: ids.caseUuid, role }));
      if (nameVal) d(dispatch("medication.setName", { uuid: medUuid, name: nameVal }));
      const dose = parseFloat(doseVal ?? "");
      if (!isNaN(dose)) d(dispatch("medication.setDose", { uuid: medUuid, dose }));
      if (routeVal) d(dispatch("medication.setRoute", { uuid: medUuid, route: routeVal }));
      const medTs = parseTimeHHMM(timeVal ?? "");
      if (medTs !== null) d(dispatch("medication.setTime", { uuid: medUuid, timestamp: medTs }));
    }
  }

  // Section 19 — R2 vital signs (3 rows)
  for (let i = 0; i < 3; i++) {
    const timeVal = fields[`r2-vs-${i}-time`] as string | undefined;
    const hasAny = (timeVal && parseTimeHHMM(timeVal) !== null) ||
      vsNumCols.some(([k]) => { const v = fields[`r2-vs-${i}-${k}`] as string | undefined; return !!v && !isNaN(parseFloat(v.replace(",", "."))); });
    if (!hasAny) continue;
    const vsUuid = createUuid();
    d(dispatch("vitals.create", { uuid: vsUuid, caseUuid: ids.caseUuid, role: 2 }));
    const ts = parseTimeHHMM(timeVal ?? "");
    if (ts !== null) d(dispatch("vitals.setTime", { uuid: vsUuid, timestamp: ts }));
    for (const [k, cmd] of vsNumCols) {
      const raw = fields[`r2-vs-${i}-${k}`] as string | undefined;
      // BP may arrive as "110 70" — split into sys / dia
      if (k === "sys" && raw?.includes(" ")) {
        const [sysStr, diaStr] = raw.split(" ");
        const sys = Number(sysStr), dia = Number(diaStr);
        if (!isNaN(sys)) d(dispatch("vitals.setSystolic",  { uuid: vsUuid, value: sys }));
        if (!isNaN(dia)) d(dispatch("vitals.setDiastolic", { uuid: vsUuid, value: dia }));
        continue;
      }
      const n = Number(raw);
      if (!isNaN(n) && raw) d(dispatch(cmd, { uuid: vsUuid, value: n }));
    }
  }

  // Section 20 — R2 ventilation parameters (3 rows)
  for (let i = 0; i < 3; i++) {
    const timeVal = fields[`r2-vent-${i}-time`] as string | undefined;
    const modeVal = fields[`r2-vent-${i}-mode`] as string | undefined;
    const hasAny = (timeVal && parseTimeHHMM(timeVal) !== null) ||
      (modeVal && !/^[.:\/]$/.test(modeVal.trim())) ||
      ventNumCols.some(([k]) => { const v = fields[`r2-vent-${i}-${k}`] as string | undefined; return !!v && !isNaN(parseFloat(v)); });
    if (!hasAny) continue;
    const ventUuid = createUuid();
    d(dispatch("ventilation.create", { uuid: ventUuid, caseUuid: ids.caseUuid, role: 2 }));
    const ts = parseTimeHHMM(timeVal ?? "");
    if (ts !== null) d(dispatch("ventilation.setTime", { uuid: ventUuid, timestamp: ts }));
    if (modeVal) d(dispatch("ventilation.setMode", { uuid: ventUuid, mode: modeVal }));
    for (const [k, cmd] of ventNumCols) {
      const n = Number(fields[`r2-vent-${i}-${k}`]);
      if (!isNaN(n) && fields[`r2-vent-${i}-${k}`]) d(dispatch(cmd, { uuid: ventUuid, value: n }));
    }
  }

  // Section 21 — R2 CPR
  const r2CprStart = fields["r2-cpr-start"] as string | undefined;
  const r2CprEnd   = fields["r2-cpr-end"]   as string | undefined;
  if (r2CprStart || r2CprEnd) {
    const cprUuid = createUuid();
    d(dispatch("cpr.create", { uuid: cprUuid, caseUuid: ids.caseUuid, role: 2 }));
    const tsStart = parseTimeHHMM(r2CprStart ?? "");
    const tsEnd   = parseTimeHHMM(r2CprEnd   ?? "");
    if (tsStart !== null) d(dispatch("cpr.setStartTime", { uuid: cprUuid, timestamp: tsStart }));
    if (tsEnd   !== null) d(dispatch("cpr.setEndTime",   { uuid: cprUuid, timestamp: tsEnd }));
  }

  // Section 17 — R2 diagnostics
  const r2DiagTextCols: [string, string][] = [
    ["r2-diag-otoscopy-r", "diagnostics.setOtoscopyRight"],
    ["r2-diag-otoscopy-l", "diagnostics.setOtoscopyLeft"],
    ["r2-diag-notes",      "diagnostics.setNotes"],
  ];
  const r2DiagLabCols: [string, string][] = [
    ["r2-diag-hg",         "labValues.setHg"],
    ["r2-diag-hct",        "labValues.setHct"],
    ["r2-diag-er",         "labValues.setRbc"],
    ["r2-diag-wbc",        "labValues.setWbc"],
    ["r2-diag-plt",        "labValues.setPlatelets"],
    ["r2-diag-na",         "labValues.setNa"],
    ["r2-diag-k",          "labValues.setK"],
    ["r2-diag-ca",         "labValues.setCa"],
    ["r2-diag-cl",         "labValues.setCl"],
    ["r2-diag-ph",         "labValues.setPh"],
    ["r2-diag-pco2",       "labValues.setPCO2"],
    ["r2-diag-po2",        "labValues.setPO2"],
    ["r2-diag-hco3",       "labValues.setHCO3"],
    ["r2-diag-lactate",    "labValues.setLactate"],
    ["r2-diag-urea",       "labValues.setUrea"],
    ["r2-diag-creatinine", "labValues.setCreatinine"],
    ["r2-diag-glucose",    "labValues.setGlucose"],
  ];
  const r2EFastVal = fields["r2-diag-efast-pos"] ? "+" : fields["r2-diag-efast-neg"] ? "-" : fields["r2-diag-efast-unk"] ? "+/-" : null;
  const r2LabNotesVal = fields["r2-diag-lab-notes"] as string | undefined;
  const hasAnyR2Diag = r2EFastVal
    || fields["r2-diag-ro"] || fields["r2-diag-ct"] || fields["r2-diag-ecg-cb"] || fields["r2-diag-us"] || fields["r2-diag-otoscopy-r-cb"] || fields["r2-diag-otoscopy-l-cb"]
    || r2DiagTextCols.some(([k]) => fields[k])
    || r2DiagLabCols.some(([k]) => fields[k]);
  const hasAnyR2LabVals = r2LabNotesVal || r2DiagLabCols.some(([k]) => fields[k]);
  if (hasAnyR2Diag) {
    const diagUuid = createUuid();
    d(dispatch("diagnostics.create", { uuid: diagUuid, caseUuid: ids.caseUuid, role: 2 }));
    if (r2EFastVal) d(dispatch("diagnostics.setEFast", { uuid: diagUuid, value: r2EFastVal }));
    if (fields["r2-diag-ro"])             d(dispatch("diagnostics.setXray",        { uuid: diagUuid, value: "+" }));
    if (fields["r2-diag-ct"])             d(dispatch("diagnostics.setCt",           { uuid: diagUuid, value: "+" }));
    if (fields["r2-diag-ecg-cb"])         d(dispatch("diagnostics.setEcg",          { uuid: diagUuid, value: "+" }));
    if (fields["r2-diag-us"])             d(dispatch("diagnostics.setUltrasound",    { uuid: diagUuid, value: "+" }));
    if (fields["r2-diag-otoscopy-r-cb"]) d(dispatch("diagnostics.setOtoscopyRight", { uuid: diagUuid, value: "+" }));
    if (fields["r2-diag-otoscopy-l-cb"]) d(dispatch("diagnostics.setOtoscopyLeft",  { uuid: diagUuid, value: "+" }));
    for (const [k, cmd] of r2DiagTextCols) {
      const val = fields[k] as string | undefined;
      if (val) d(dispatch(cmd, { uuid: diagUuid, value: val }));
    }
  }
  if (hasAnyR2LabVals) {
    const labValUuid = createUuid();
    d(dispatch("labValues.create", { uuid: labValUuid, caseUuid: ids.caseUuid, role: 2 }));
    if (r2LabNotesVal) d(dispatch("labValues.setNotes", { uuid: labValUuid, value: r2LabNotesVal }));
    for (const [k, cmd] of r2DiagLabCols) {
      const raw = fields[k] as string | undefined;
      if (!raw) continue;
      // Filled PDFs emit "Label: value" as a single text item — strip the prefix
      const stripped = raw.includes(":") ? raw.split(":").slice(1).join(":").trim() : raw;
      const n = parseFloat(stripped);
      if (!isNaN(n)) d(dispatch(cmd, { uuid: labValUuid, value: n }));
    }
  }

  // Burn zones — detected red regions → case.setTraumaBurnItem
  const burnJson = fields[BURN_ZONES_KEY] as string | undefined;
  if (burnJson) {
    try {
      const zones = JSON.parse(burnJson) as string[];
      for (const zone of zones) {
        d(dispatch("case.setTraumaBurnItem", { uuid: ids.caseUuid, zone, value: true }));
      }
    } catch {
      // Malformed JSON — skip burn zones silently
    }
  }

  // Body injury diagram — detected X marks → case.addInjuryMark
  const injuryJson = fields[BODY_INJURY_MARKS_KEY] as string | undefined;
  if (injuryJson) {
    try {
      const marks = JSON.parse(injuryJson) as { x: number; y: number }[];
      for (const { x, y } of marks) {
        d(dispatch("case.addInjuryMark", { uuid: ids.caseUuid, markId: createUuid(), x, y }));
      }
    } catch {
      // Malformed JSON — skip injury marks silently
    }
  }

  // Section 23 — R2 medications (left role=2, right role=12; 5 rows each)
  for (const [side, role] of [["left", 2], ["right", 12]] as [string, number][]) {
    for (let i = 0; i < 5; i++) {
      const prefix = `p2-med-${side}-${i}`;
      const nameVal  = fields[`${prefix}-name`]  as string | undefined;
      const doseVal  = fields[`${prefix}-dose`]  as string | undefined;
      const routeVal = fields[`${prefix}-route`] as string | undefined;
      const timeVal  = fields[`${prefix}-time`]  as string | undefined;
      if (!nameVal && !doseVal && !routeVal && !timeVal) continue;
      const medUuid = createUuid();
      d(dispatch("medication.create", { uuid: medUuid, caseUuid: ids.caseUuid, role }));
      if (nameVal) d(dispatch("medication.setName", { uuid: medUuid, name: nameVal }));
      const dose = parseFloat(doseVal ?? "");
      if (!isNaN(dose)) d(dispatch("medication.setDose", { uuid: medUuid, dose }));
      if (routeVal) d(dispatch("medication.setRoute", { uuid: medUuid, route: routeVal }));
      const medTs = parseTimeHHMM(timeVal ?? "");
      if (medTs !== null) d(dispatch("medication.setTime", { uuid: medUuid, timestamp: medTs }));
    }
  }

  await Promise.all(pending);
}
